// Submissions API — mirrors /app/mobile/src/lib/submissions-api.ts
import { apiFetch, BACKEND_URL, uploadWithProgress } from './api';
import { KEYS, storage } from './storage';

export const listEventSubmissions = (eventId, params = {}) => {
  const q = new URLSearchParams();
  if (params.status) q.set('status', params.status);
  if (params.media_kind) q.set('media_kind', params.media_kind);
  if (params.contributor_id) q.set('contributor_id', params.contributor_id);
  if (params.sort) q.set('sort', params.sort);
  if (params.cursor) q.set('cursor', params.cursor);
  if (params.limit) q.set('limit', String(params.limit));
  return apiFetch(`/api/v1/events/${eventId}/submissions?${q.toString()}`);
};

export const listMySubmissions = (eventId) =>
  apiFetch(`/api/v1/events/${eventId}/submissions/mine`);

export const getSubmission = (subId) =>
  apiFetch(`/api/v1/submissions/${subId}`);

export const withdrawSubmission = (subId) =>
  apiFetch(`/api/v1/submissions/${subId}/withdraw`, { method: 'POST' });

export const reviewSubmission = (subId, payload) =>
  apiFetch(`/api/v1/submissions/${subId}/review`, { method: 'POST', body: JSON.stringify(payload) });

export const bulkReviewSubmissions = (eventId, submissionIds, actions) =>
  apiFetch(`/api/v1/events/${eventId}/submissions/bulk`, {
    method: 'POST',
    body: JSON.stringify({ submission_ids: submissionIds, actions }),
  });

export const softDeleteSubmission = (subId) =>
  apiFetch(`/api/v1/submissions/${subId}/delete`, { method: 'POST' });

export const restoreSubmission = (subId) =>
  apiFetch(`/api/v1/submissions/${subId}/restore`, { method: 'POST' });

export const unarchiveSubmission = (subId) =>
  apiFetch(`/api/v1/submissions/${subId}/unarchive`, { method: 'POST' });

export const listSimilarSubmissions = (subId, threshold = 6) =>
  apiFetch(`/api/v1/submissions/${subId}/similar?threshold=${threshold}`);

export const listEventActivity = (eventId, limit = 20) =>
  apiFetch(`/api/v1/events/${eventId}/activity?limit=${limit}`);

export const computeStats = (eventId) =>
  apiFetch(`/api/v1/events/${eventId}/submissions/stats`);

export async function fetchSubmissionThumbnailDataUrl(subId) {
  try {
    const at = storage.get(KEYS.accessToken);
    const resp = await fetch(`${BACKEND_URL}/api/v1/submissions/${subId}/thumbnail`, {
      headers: at ? { Authorization: `Bearer ${at}` } : {},
    });
    if (!resp.ok) return null;
    const blob = await resp.blob();
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result);
      reader.readAsDataURL(blob);
    });
  } catch { return null; }
}

// ----- Upload Session API -----

export async function createUploadSession(eventId, payload) {
  return apiFetch(`/api/v1/events/${eventId}/uploads`, {
    method: 'POST', body: JSON.stringify(payload),
  });
}

/**
 * Send raw bytes for an upload session using XHR for progress tracking.
 * Matches mobile `sendSubmissionBytes`.
 */
export function sendSubmissionBytes(eventId, sessionId, file, filename, onProgress, offset = 0) {
  return new Promise((resolve, reject) => {
    const at = storage.get(KEYS.accessToken);
    const qs = offset > 0 ? `?offset=${offset}` : '';
    const url = `${BACKEND_URL}/api/v1/events/${eventId}/uploads/${sessionId}/bytes${qs}`;
    const xhr = new XMLHttpRequest();
    xhr.open('POST', url, true);
    if (at) xhr.setRequestHeader('Authorization', `Bearer ${at}`);
    xhr.upload.onprogress = (e) => {
      if (onProgress && e.lengthComputable) onProgress(e.loaded, e.total);
    };
    xhr.onerror = () => reject(new Error('Network error while uploading.'));
    xhr.onabort = () => reject(new Error('Upload cancelled.'));
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        try { resolve(JSON.parse(xhr.responseText)); }
        catch { reject(new Error('Bad server response.')); }
      } else {
        try {
          const body = JSON.parse(xhr.responseText);
          const err = new Error(body?.error?.message || 'Upload failed.');
          err.code = body?.error?.code;
          reject(err);
        } catch { reject(new Error(`Upload failed (${xhr.status}).`)); }
      }
    };
    const form = new FormData();
    form.append('file', file, filename);
    xhr.send(form);
  });
}

export async function completeSubmissionSession(eventId, sessionId) {
  return apiFetch(`/api/v1/events/${eventId}/uploads/${sessionId}/complete`, { method: 'POST' });
}

export async function cancelUploadSession(eventId, sessionId) {
  return apiFetch(`/api/v1/events/${eventId}/uploads/${sessionId}/cancel`, { method: 'POST' });
}

// ----- Resumable Upload (Phase 9 #5) -----

export const getUploadSessionStatus = (eventId, sessionId) =>
  apiFetch(`/api/v1/events/${eventId}/uploads/${sessionId}/status`);

const MAX_RESUME_ATTEMPTS = 4;
const RESUME_BACKOFF_MS = [400, 1200, 3000, 6000];

export async function uploadWithResume(eventId, sessionId, file, filename, onProgress) {
  const total = file.size;
  let attempt = 0;
  let offset = 0;
  let lastErr = null;
  while (attempt < MAX_RESUME_ATTEMPTS) {
    try {
      const slice = offset === 0 ? file : file.slice(offset);
      const result = await sendSubmissionBytes(
        eventId, sessionId, slice, filename,
        (loaded) => onProgress?.(offset + loaded, total),
        offset,
      );
      return result;
    } catch (err) {
      lastErr = err;
      attempt++;
      if (attempt >= MAX_RESUME_ATTEMPTS) break;
      await new Promise((r) => setTimeout(r, RESUME_BACKOFF_MS[attempt - 1] || 6000));
      try {
        const st = await getUploadSessionStatus(eventId, sessionId);
        offset = st.received_bytes;
        onProgress?.(offset, total);
        if (st.status === 'UPLOADED' || offset >= total) {
          return { id: sessionId, status: 'UPLOADED', size_bytes_actual: offset };
        }
      } catch { /* probe failure — retry from current offset */ }
    }
  }
  throw lastErr instanceof Error ? lastErr : new Error('Upload failed after retries.');
}

// Legacy wrappers for backward compat
export async function sendUploadBytes(uploadUrl, file, onProgress) {
  const at = storage.get(KEYS.accessToken);
  return uploadWithProgress(uploadUrl, 'file', file, onProgress, at);
}

export async function completeUpload(completeUrl) {
  return apiFetch(completeUrl, { method: 'POST' });
}
